import * as THREE from 'three';
import { DirtyRange, FOG_FRAG, FOG_VERT, fxUniforms, quadGeometry } from './common';

/**
 * Gravity wells on the road: one ground-aligned instanced draw. Each well opens, swirls a dark
 * funnel with pink spiral arms and a bright event-horizon rim, then collapses. Premultiplied alpha:
 * the funnel darkens the stone, the arms add light. No draw when none are alive.
 */
export class GravityWells {
  readonly mesh: THREE.Mesh;
  private readonly shape: THREE.InstancedBufferAttribute;
  private readonly life: THREE.InstancedBufferAttribute;
  private readonly dirty = new DirtyRange();
  private readonly ends: Float32Array;
  private count = 0;

  constructor(color: number, readonly capacity = 12) {
    const g = quadGeometry();
    this.shape = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(THREE.DynamicDrawUsage);
    this.life = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('iShape', this.shape); g.setAttribute('iLife', this.life);
    g.instanceCount = 0;
    this.ends = new Float32Array(capacity);
    this.mesh = new THREE.Mesh(g, new THREE.ShaderMaterial({
      uniforms: { ...fxUniforms, uColor: { value: new THREE.Color(color) } },
      transparent: true, depthWrite: false,
      blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
      vertexShader: /* glsl */ `
        attribute vec4 iShape; attribute vec4 iLife;
        uniform float uTime;
        varying vec2 vUv; varying float vAge, vLeft, vSeed, vLevel;
        ${FOG_VERT}
        void main() {
          vAge = uTime - iLife.x; vLeft = iLife.y - uTime; vSeed = iShape.w; vLevel = iLife.z;
          if (vLeft <= 0.0 || iShape.z <= 0.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
          vUv = uv * 2.0 - 1.0;
          vec4 mv = modelViewMatrix * vec4(iShape.x + position.x * iShape.z * 2.4, 0.05 + vSeed * 0.004, iShape.y - position.y * iShape.z * 2.4, 1.0);
          FOG_FROM_MV(mv)
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform float uTime; uniform vec3 uColor;
        varying vec2 vUv; varying float vAge, vLeft, vSeed, vLevel;
        ${FOG_FRAG}
        void main() {
          // open over 0.25 s, collapse over the last 0.3 s
          float open = smoothstep(0.0, 0.25, vAge) * smoothstep(0.0, 0.3, vLeft);
          float d = length(vUv) / max(open, 0.05);
          if (d > 1.0) discard;
          float a = atan(vUv.y, vUv.x);
          // spiral arms winding inward faster near the centre
          float spin = uTime * (2.2 + vLevel * 0.3) + vSeed * 6.0;
          float arms = pow(max(0.0, sin(a * 3.0 + log(max(d, 0.02)) * 7.0 + spin * 2.0)), 6.0) * smoothstep(1.0, 0.35, d) * smoothstep(0.08, 0.25, d);
          float rim = exp(-pow((d - 0.18) * 22.0, 2.0));
          float edge = exp(-pow((d - 0.97) * 30.0, 2.0)) * 0.6;
          float funnel = smoothstep(1.0, 0.1, d);
          vec3 glow = (uColor * (arms * 1.3 + edge) + mix(uColor, vec3(1.0), 0.5) * rim * 1.6) * open;
          float dark = funnel * 0.75 * open;
          gl_FragColor = vec4(glow * fogKeep(), dark);
        }`,
    }));
    this.mesh.name = 'gravityWells'; this.mesh.renderOrder = 4;
    this.mesh.visible = false; this.mesh.frustumCulled = false;
  }

  add(x: number, z: number, radius: number, duration: number, level: number) {
    const now = fxUniforms.uTime.value;
    let slot = -1, soonest = Infinity;
    for (let i = 0; i < this.capacity; i++) {
      if (this.ends[i] <= now) { slot = i; break; }
      if (this.ends[i] < soonest) { soonest = this.ends[i]; slot = i; }
    }
    this.ends[slot] = now + duration;
    this.shape.setXYZW(slot, x, z, radius / 2.4 * 2, Math.random());
    this.life.setXYZW(slot, now, now + duration, level, 0);
    this.dirty.mark(slot);
    this.count = Math.max(this.count, slot + 1);
  }

  flush() {
    const now = fxUniforms.uTime.value;
    while (this.count > 0 && this.ends[this.count - 1] <= now) this.count--;
    (this.mesh.geometry as THREE.InstancedBufferGeometry).instanceCount = this.count;
    this.mesh.visible = this.count > 0;
    this.dirty.flush([this.shape, this.life]);
  }
}
